import React, { useEffect, useCallback } from 'react';
import {
  View,
  Text,
  Pressable,
  type ViewStyle,
  type TextStyle,
  useColorScheme,
  Animated,
  Platform,
} from 'react-native';
import { tokens } from '../../design-system/tokens';

export type ToastVariant = 'info' | 'success' | 'error' | 'warning';

export interface ToastProps {
  id: string;
  variant?: ToastVariant;
  message: string;
  duration?: number;
  onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  variant = 'info',
  message,
  duration = 3000,
  onDismiss,
}) => {
  const scheme = useColorScheme();
  const colorScheme = scheme === 'dark' ? 'dark' : 'light';
  const colors = tokens.color.semantic[colorScheme];

  const translateY = React.useRef(new Animated.Value(-100)).current;
  const opacity = React.useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(translateY, {
        toValue: 0,
        tension: 80,
        friction: 10,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(opacity, {
        toValue: 1,
        duration: tokens.motion.duration.standard,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start();

    if (duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, translateY, opacity]);

  const handleDismiss = useCallback(() => {
    Animated.parallel([
      Animated.timing(translateY, {
        toValue: -100,
        duration: tokens.motion.duration.fast,
        useNativeDriver: Platform.OS !== 'web',
      }),
      Animated.timing(opacity, {
        toValue: 0,
        duration: tokens.motion.duration.fast,
        useNativeDriver: Platform.OS !== 'web',
      }),
    ]).start(() => {
      onDismiss(id);
    });
  }, [id, onDismiss, translateY, opacity]);

  const containerStyles = [
    styles.container,
    { backgroundColor: colors.surface.primary },
    { borderLeftColor: getVariantColor(variant, colorScheme) },
  ] as ViewStyle[];

  return (
    <Animated.View
      style={[
        styles.wrapper,
        {
          transform: [{ translateY }],
          opacity,
        },
      ]}
    >
      <Pressable
        onPress={handleDismiss}
        style={containerStyles}
        accessibilityRole="alert"
        accessibilityLiveRegion="polite"
      >
        <View style={styles.content}>
          <View
            style={[styles.iconContainer, { backgroundColor: getVariantColor(variant, colorScheme) }]}
          >
            <Text style={styles.icon}>{getVariantIcon(variant)}</Text>
          </View>
          <Text style={[styles.message, { color: colors.text.primary }]} numberOfLines={3}>
            {message}
          </Text>
        </View>
      </Pressable>
    </Animated.View>
  );
};

function getVariantColor(variant: ToastVariant, colorScheme: 'light' | 'dark'): string {
  const colors = tokens.color.semantic[colorScheme];
  switch (variant) {
    case 'success':
      return colors.accent.green;
    case 'error':
      return colors.accent.rose;
    case 'warning':
      return colors.accent.amber;
    case 'info':
    default:
      return colors.accent.indigo;
  }
}

function getVariantIcon(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return '✓';
    case 'error':
      return '✕';
    case 'warning':
      return '⚠';
    case 'info':
    default:
      return 'ℹ';
  }
}

const styles = {
  wrapper: {
    marginBottom: tokens.spacing.sm,
  } satisfies ViewStyle,

  container: {
    borderRadius: tokens.radius.md,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
    minHeight: 56,
  } satisfies ViewStyle,

  content: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: tokens.spacing.md,
    gap: tokens.spacing.md,
  } satisfies ViewStyle,

  iconContainer: {
    width: 28,
    height: 28,
    borderRadius: tokens.radius.pill,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,

  icon: {
    fontSize: 16,
    color: '#FFFFFF',
    fontWeight: '700' as TextStyle['fontWeight'],
  } satisfies TextStyle,

  message: {
    flex: 1,
    fontFamily: tokens.typography.fontFamily.base,
    fontSize: tokens.typography.fontSize.body,
    fontWeight: '400' as TextStyle['fontWeight'],
    lineHeight: tokens.typography.fontSize.body * tokens.typography.lineHeight.normal,
  } satisfies TextStyle,
};
